/**
 * Composant Soft Prompt FCM
 *
 * Best practices Aimtell appliquées :
 *  - Demande la permission UNIQUEMENT après une action explicite de l'utilisateur
 *  - Affiche d'abord la valeur (ce que les notifications vont apporter) avant
 *    le prompt système — ça permet de re-demander plus tard si l'utilisateur
 *    refuse le soft prompt (le prompt natif, lui, est définitif si refusé).
 *  - Ne s'affiche pas si l'utilisateur n'est pas authentifié, si la permission
 *    a déjà été accordée ou refusée définitivement, ou si le navigateur ne
 *    supporte pas FCM (Safari iOS < 16.4, mode privé, etc.).
 *  - Persiste le "dismiss" 7 jours dans localStorage pour ne pas re-spammer.
 */
import React, { useEffect, useState } from 'react';
import { Bell, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import fcmService from '../services/fcmService';
import { useAuth } from '../context/AuthContext';

const DISMISS_KEY = 'fcm_soft_prompt_dismissed_at';
const DISMISS_TTL_DAYS = 7;

function shouldShowPrompt() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return true;
    const dismissedAt = parseInt(raw, 10);
    if (!dismissedAt) return true;
    const ageDays = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return ageDays > DISMISS_TTL_DAYS;
  } catch {
    return true;
  }
}

export default function PushNotificationPrompt() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!fcmService.isConfigured() || !fcmService.isSupported()) return;
    const perm = fcmService.getPermissionState();
    if (perm === 'granted') {
      // Restaurer le listener foreground silencieusement
      fcmService.restoreIfGranted();
      return;
    }
    if (perm === 'denied') return; // navigateur a refusé définitivement
    if (!shouldShowPrompt()) return;

    // Petit délai pour laisser la page finir de charger (best practice Aimtell §2)
    const t = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(t);
  }, [user]);

  const handleEnable = async () => {
    setLoading(true);
    const result = await fcmService.enable();
    setLoading(false);

    if (result?.token) {
      toast.success('Notifications activées', {
        description: 'Vous recevrez les alertes ARS 1000, audits et primes PSE.',
      });
      setVisible(false);
      return;
    }

    if (result?.error === 'permission_denied') {
      toast.error('Permission refusée', {
        description: 'Vous pouvez l\'autoriser dans les paramètres du navigateur.',
      });
    } else if (result?.error === 'unsupported') {
      toast.error('Navigateur non supporté', {
        description: 'Safari iOS < 16.4 et navigateurs sans Push API ne sont pas supportés.',
      });
    } else if (result?.error === 'not_configured') {
      toast.error('Configuration Firebase manquante côté frontend.');
    } else {
      toast.error('Activation impossible', { description: result?.message || 'Erreur inconnue' });
    }
    setVisible(false);
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* quota */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 max-w-sm rounded-xl border border-emerald-200 bg-white shadow-2xl shadow-emerald-900/10 animate-in slide-in-from-bottom-4 fade-in duration-300"
      data-testid="fcm-soft-prompt"
    >
      <div className="relative p-4">
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-2 top-2 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label="Fermer"
          data-testid="fcm-soft-prompt-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <Bell className="h-5 w-5" />
          </div>

          <div className="flex-1 pr-4">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
              Restez informé en temps réel
              <Sparkles className="h-3.5 w-3.5 text-amber-500" />
            </h3>
            <p className="mt-1 text-xs text-gray-600 leading-relaxed">
              Recevez les alertes ARS 1000, échéances d'audit, validations PDC et primes PSE — même quand l'application est fermée.
            </p>

            <div className="mt-3 flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleEnable}
                disabled={loading}
                className="h-8 bg-emerald-600 hover:bg-emerald-700 text-xs"
                data-testid="fcm-enable-btn"
              >
                {loading ? 'Activation…' : 'Activer'}
              </Button>
              <button
                type="button"
                onClick={handleDismiss}
                className="text-xs text-gray-500 hover:text-gray-700"
                data-testid="fcm-soft-prompt-later"
              >
                Plus tard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
